import { BubbleMenu, BubbleMenuProps, Range } from '@tiptap/react'
import { Editor } from '@tiptap/react/src/Editor'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import styles from './LinkEditor.module.scss'

import Button from '~elements/Button'
import { useTimeout } from '~hooks/useTimeout'
import { PortalRoots } from '~lib/constants'
import { pasteRegexExact } from '~lib/editor/extensions/link'
import getMarkRange from '~lib/editor/helpers/getMarkRange'
import { getSelectedText } from '~lib/editor/helpers/selection'
import useGetElementById from '~lib/hooks/useGetElementById'
import InputField from '~lib/ui-kit/InputField'

interface LinkEditorProps {
  editor: Editor
  opened: boolean
  onClose: () => void
}

function shouldShowLinkEditor() {
  return true
}

const LinkEditor: FC<React.PropsWithChildren<LinkEditorProps>> = ({ editor, opened, onClose }) => {
  const [href, setHref] = useState('')
  const [title, setTitle] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const { setTimeout } = useTimeout()

  const modalRoot = useGetElementById(PortalRoots.MODAL_ROOT)

  const tippyConfig: BubbleMenuProps['tippyOptions'] = useMemo(
    () => ({ placement: 'bottom', interactive: true, appendTo: modalRoot }),
    [modalRoot]
  )

  const handleHrefChange = useCallback((ev: any) => {
    setHref(ev.target.value.trim())
  }, [])

  const handleTitleChange = useCallback((ev: any) => {
    setTitle(ev.target.value)
  }, [])

  useClickAway(formRef, () => {
    onClose()
  })

  useEffect(() => {
    if (opened) {
      if (editor.isActive('link')) {
        const { state, schema } = editor
        const { href = '' } = editor.getAttributes('link')
        const range = getMarkRange(editor, state.selection.from, schema.marks.link)
        const text = state.doc.textBetween(range.from, range.to).trim()
        setTitle(text === href ? '' : text)
        setHref(href)
      } else {
        const text = getSelectedText(editor)

        if (!text) return // TODO add logging

        // See:https://stackoverflow.com/questions/13586786/regexp-test-returns-different-result-for-same-str-depending-on-how-where-i
        // eslint-disable-next-line unicorn/prefer-regexp-test
        if (text.match(pasteRegexExact)) {
          setHref(text)
        } else {
          setTitle(text)
        }
      }

      setTimeout(() => {
        const input = formRef.current?.querySelector<HTMLInputElement>('input[name="link-title"]')
        input?.focus()
      }, 100)
    } else {
      setTitle('')
      setHref('')
    }
  }, [editor, opened, setTimeout])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!href) {
      onClose()
      return
    }

    let range: Range

    if (editor.isActive('link')) {
      range = getMarkRange(editor, editor.state.selection.from, editor.schema.marks.link)
    } else {
      const { from, to } = editor.state.selection
      range = { from, to }
    }

    const linkText = title?.trim() || href
    const nextSelection = range.from + linkText.length

    editor
      .chain()
      .focus()
      .setTextSelection(range)
      .deleteSelection()
      .insertContent(linkText)
      .setTextSelection({
        ...range,
        to: nextSelection,
      })
      .setMark('link', { href })
      .setTextSelection(nextSelection)
      .run()

    onClose()
  }

  return (
    <BubbleMenu editor={editor} pluginKey="link-editor" shouldShow={shouldShowLinkEditor} tippyOptions={tippyConfig}>
      {opened && (
        <form onSubmit={handleSubmit} className={styles.root} ref={formRef}>
          <InputField
            name="link-title"
            value={title}
            onChange={handleTitleChange}
            className={styles.input}
            animated={false}
            placeholder="Text"
          />
          <InputField
            name="link-href"
            value={href}
            onChange={handleHrefChange}
            className={styles.input}
            animated={false}
            placeholder="Link"
          />
          <div className={styles.controls}>
            <Button className={styles.remove} onClick={onClose} size="m" variant="plain">
              Cancel
            </Button>
            <Button type="submit" size="m" variant="highlight">
              Save
            </Button>
          </div>
        </form>
      )}
    </BubbleMenu>
  )
}

export default LinkEditor
